// LayoutAccordion — Portals-3 stacked-card layout for Partner Portal.
//
// Top metric strip (4 cards) + "My direct introductions" feature card
// (when relevant, expanded by default) + per-introducer collapsible
// cards. Each card expands to show its client grid; clicking
// "View all →" inside a card hands control back to the columns layout
// via onJumpToColumns().
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Users, TrendingUp, DollarSign, Star, Wallet, ArrowRight } from "lucide-react";

import { formatCurrency } from "@/lib/formatters";

const DIRECT_KEY = "__direct__";

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4">
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-gray-500">
        <Icon className={`w-3.5 h-3.5 ${accent || "text-[#bd9a59]"}`} /> {label}
      </div>
      <div className="text-xl font-bold text-[#021d49]">{value}</div>
    </div>
  );
}

function ClientGrid({ clients }) {
  if (!clients || clients.length === 0) {
    return <div className="text-xs text-gray-500 py-2">No clients yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {clients.map((c) => (
        <div key={c.id} className="border border-gray-200 rounded p-2.5 hover:border-[#bd9a59]/50 transition-colors">
          <div className="text-sm font-medium text-[#021d49] truncate">{c.name || c.company_name || "—"}</div>
          <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-2">
            <span>{c.deal_count || 0} deal{(c.deal_count || 0) === 1 ? "" : "s"}</span>
            <span>·</span>
            <span>{formatCurrency(c.pipeline_total || 0)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function IntroducerCard({
  title,
  badge,
  subtitleLeft,
  subtitleRight,
  metaPills,
  expandedDefault,
  loadClients,
  cachedClients,
  onJumpToColumns,
  forceOpenOnMount,
}) {
  const [open, setOpen] = useState(!!expandedDefault);
  const [clients, setClients] = useState(cachedClients);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setClients(cachedClients);
  }, [cachedClients]);

  // If the card defaults open, pre-fetch its clients once.
  useEffect(() => {
    if (!forceOpenOnMount) return;
    if (clients || loading || !loadClients) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await loadClients();
        if (!cancelled) setClients(data?.clients || []);
      } catch (_) { /* toast raised at parent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [forceOpenOnMount, clients, loading, loadClients]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !clients && !loading && loadClients) {
      setLoading(true);
      try {
        const data = await loadClients();
        setClients(data?.clients || []);
      } catch (_) { /* ignore */ }
      finally { setLoading(false); }
    }
  };

  return (
    <div className={`bg-white border rounded-md overflow-hidden ${badge ? "border-[#bd9a59]/40" : "border-gray-200"}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {badge && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#bd9a59] font-semibold"><Star className="w-3 h-3" />{badge}</span>}
            <div className="text-sm font-semibold text-[#021d49] truncate">{title}</div>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{subtitleLeft}</span>
            <span>·</span>
            <span>{subtitleRight}</span>
            {metaPills}
          </div>
        </div>
        {onJumpToColumns && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onJumpToColumns(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onJumpToColumns(); } }}
            className="inline-flex items-center gap-1 text-[11px] text-[#bd9a59] hover:text-[#021d49] font-medium px-2 py-1 rounded hover:bg-[#bd9a59]/10"
          >
            View all <ArrowRight className="w-3 h-3" />
          </span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100">
          {loading ? (
            <div className="text-xs text-gray-500 py-3">Loading clients…</div>
          ) : (
            <ClientGrid clients={clients} />
          )}
        </div>
      )}
    </div>
  );
}

export default function LayoutAccordion({ dashboard, clientsCache, fetchers, onJumpToColumns }) {
  const { partner = {}, introducers = [], direct_introductions = {}, totals = {} } = dashboard || {};
  // Portals-5 (2026-06-21) — mirror of LayoutColumns: hide the direct row
  // entirely for partners who aren't also introducers AND have no active
  // direct deals. Keeps the card list cleaner for pure-downline partners.
  const hasDirectActivity = (direct_introductions.active_deal_count || 0) > 0
    || (direct_introductions.client_count || 0) > 0;
  const showDirect = hasDirectActivity
    && !(
      (direct_introductions.active_deal_count || 0) === 0
      && partner.is_also_introducer === false
    );

  // Stable references for the "loadClients" function passed to each card —
  // memoised so cards don't re-run their pre-fetch effect when the parent
  // re-renders for an unrelated state change (toggling a different card).
  const directLoader = useMemo(() => async () => fetchers.fetchDirectClients(), [fetchers]);
  const makeIntroLoader = useMemo(() => (id) => async () => fetchers.fetchIntroducerClients(id), [fetchers]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Top metrics strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={Users} label="Introducers" value={totals.introducer_count || 0} />
        <MetricCard icon={TrendingUp} label="Active deals" value={totals.active_deal_count_all || 0} />
        <MetricCard icon={DollarSign} label="Pipeline total" value={formatCurrency(totals.pipeline_total_all || 0)} />
        <MetricCard icon={Wallet} label="YTD commission" value={formatCurrency(partner.ytd_commission_earned || totals.ytd_partner_commission_earned || 0)} />
      </div>

      {/* Direct introductions (first-class) */}
      {showDirect && (
        <IntroducerCard
          title="My direct introductions"
          badge="Direct"
          subtitleLeft={`${direct_introductions.client_count || 0} client${(direct_introductions.client_count || 0) === 1 ? "" : "s"}`}
          subtitleRight={`${direct_introductions.active_deal_count || 0} active · ${formatCurrency(direct_introductions.pipeline_total || 0)}`}
          metaPills={null}
          expandedDefault
          forceOpenOnMount
          loadClients={directLoader}
          cachedClients={clientsCache[DIRECT_KEY]?.clients}
          onJumpToColumns={onJumpToColumns ? () => onJumpToColumns(DIRECT_KEY) : null}
        />
      )}

      {/* Downline introducers */}
      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 px-1">Downline introducers</div>
        {introducers.length === 0 && (
          <div className="bg-white border border-dashed border-gray-200 rounded-md p-6 text-sm text-gray-500 text-center">
            No introducers in your downline yet. Solara will add them as they join.
          </div>
        )}
        {introducers.map((intr) => {
          const soonPill = (intr.settling_soon_count || 0) > 0 ? (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
              {intr.settling_soon_count} settling soon
            </span>
          ) : null;
          return (
            <IntroducerCard
              key={intr.id}
              title={intr.name || "—"}
              subtitleLeft={`${intr.client_count || 0} clients · ${intr.active_deal_count || 0} active`}
              subtitleRight={`${formatCurrency(intr.pipeline_total || 0)} pipeline · Your share est. ${formatCurrency(intr.your_share_estimate || 0)}`}
              metaPills={soonPill}
              loadClients={makeIntroLoader(intr.id)}
              cachedClients={clientsCache[intr.id]?.clients}
              onJumpToColumns={onJumpToColumns ? () => onJumpToColumns(intr.id) : null}
            />
          );
        })}
      </div>
    </div>
  );
}
